import { useState } from 'react';
import { theme } from '../../theme.js';
import { useAuth, useAccess } from '../../context/AuthContext.js';
import {
  useTeams, useCreateTeam,
  useTeamMembers, useAddTeamMember, useRemoveTeamMember, useSetTeamDisabled,
  useOrgMembers,
} from '../../lib/queries.js';
import { Icon } from '../../components/Icon.js';
import { LeaveTag } from '../../components/LeaveCard.js';
import type { Team, OrgMember, TeamRole } from '@keetcheck/shared';

export function TeamsPage() {
  const { user } = useAuth();
  const { isAdmin, ledTeam } = useAccess();
  const orgId = user?.orgId ?? '';

  const { data: allTeams = [], isLoading } = useTeams(orgId);
  const createMut = useCreateTeam(orgId);
  // A team's leader only sees (and manages) their own team.
  const teams = isAdmin ? allTeams : allTeams.filter((t) => t.id === ledTeam?.id);

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [expandedTeam, setExpandedTeam] = useState<string | null>(isAdmin ? null : ledTeam?.id ?? null);

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    createMut.mutate(
      { name: name.trim() },
      { onSuccess: () => { setName(''); setShowForm(false); } },
    );
  }

  return (
    <div style={{ flex: 1, background: theme.surface }}>
      {/* toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px', background: theme.bg,
        borderBottom: `1px solid ${theme.border}`,
      }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: theme.textDim }}>
          {teams.length} team{teams.length !== 1 ? 's' : ''}
        </span>
        {isAdmin && (
          <button onClick={() => setShowForm(!showForm)} style={addBtn}>
            <Icon name="plus" size={15} /> Add team
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleCreate} style={{
          padding: '12px 16px', background: theme.surface2,
          borderBottom: `1px solid ${theme.border}`,
          display: 'flex', gap: 8,
        }}>
          <input
            required value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Team name" style={{ ...inputStyle, flex: 1 }}
          />
          <button type="submit" disabled={createMut.isPending} style={smallSubmit}>
            {createMut.isPending ? '…' : 'Create'}
          </button>
        </form>
      )}

      {isLoading && <EmptyMsg>Loading…</EmptyMsg>}
      {!isLoading && teams.length === 0 && <EmptyMsg>No teams yet.</EmptyMsg>}

      {teams.map((team) => (
        <TeamPanel
          key={team.id}
          team={team}
          orgId={orgId}
          isAdmin={isAdmin}
          expanded={expandedTeam === team.id}
          onToggle={() => setExpandedTeam(expandedTeam === team.id ? null : team.id)}
        />
      ))}
    </div>
  );
}

function TeamPanel({
  team, orgId, isAdmin, expanded, onToggle,
}: {
  team: Team; orgId: string; isAdmin: boolean; expanded: boolean; onToggle: () => void;
}) {
  const { data: members = [] } = useTeamMembers(orgId, team.id);
  const { data: orgMembers = [] } = useOrgMembers(orgId);
  const addMut = useAddTeamMember(orgId, team.id);
  const removeMut = useRemoveTeamMember(orgId, team.id);
  const disableMut = useSetTeamDisabled(orgId, team.id);

  const [selectedUserId, setSelectedUserId] = useState('');
  const [role, setRole] = useState<TeamRole>('member');
  const [showAddForm, setShowAddForm] = useState(false);

  // One team per person. Leaders can add people who have no team yet;
  // admins can also pick someone on another team, which moves them.
  const available = orgMembers.filter((m: OrgMember) =>
    m.teamId !== team.id && (isAdmin || m.teamId === null));
  // Email only tells apart two people with the same name; otherwise it just makes the list too wide.
  const sameName = (m: OrgMember) => available.some((o) => o.userId !== m.userId && o.displayName === m.displayName);
  const leaveOf = (userId: string) => orgMembers.find((m) => m.userId === userId)?.leave ?? null;
  const awayNow = members.filter((m) => leaveOf(m.userId)?.active).length;

  function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedUserId) return;
    addMut.mutate(
      { userId: selectedUserId, role },
      { onSuccess: () => { setSelectedUserId(''); setShowAddForm(false); } },
    );
  }

  return (
    <div style={{ borderBottom: `1px solid ${theme.border}` }}>
      {/* header row */}
      <div
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '13px 16px', cursor: 'pointer', background: theme.surface,
        }}
      >
        <Icon name={expanded ? 'chevronD' : 'chevron'} size={15} style={{ color: theme.textMuted }} />
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: team.disabledAt ? theme.textMuted : theme.text }}>{team.name}</span>
          {team.disabledAt && (
            <span style={{
              marginLeft: 8, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
              background: theme.status.muted.bg, color: theme.status.muted.fg,
            }}>DISABLED</span>
          )}
          <span style={{ fontSize: 12, color: theme.textMuted, marginLeft: 8 }}>
            {members.length} member{members.length !== 1 ? 's' : ''}
            {awayNow > 0 && <span style={{ color: theme.status.warn.fg, fontWeight: 600 }}> · {awayNow} on leave</span>}
          </span>
        </div>
        <button aria-label="Add a member to this team" title="Add a member"
          onClick={(e) => {
            e.stopPropagation();
            setShowAddForm(!showAddForm);
            if (!expanded) onToggle();
          }}
          style={addBtnSmall}
        >
          <Icon name="plus" size={13} />
        </button>
      </div>

      {expanded && (
        <div style={{ background: theme.surface2 }}>
          {isAdmin && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px',
              borderBottom: `1px solid ${theme.border}`, fontSize: 12, color: theme.textMuted,
            }}>
              <span style={{ flex: 1 }}>
                {team.disabledAt
                  ? 'Disabled: out of schedule rotations (its slot goes unchecked) and can’t start checks. Members stay.'
                  : 'Disable to pause this team — it keeps its members and can be re-enabled any time.'}
              </span>
              <button
                onClick={() => disableMut.mutate(!team.disabledAt)}
                disabled={disableMut.isPending}
                style={{
                  padding: '6px 12px', borderRadius: 8, flexShrink: 0, cursor: 'pointer', fontFamily: theme.font,
                  fontSize: 12, fontWeight: 600, border: `1px solid ${theme.border}`,
                  background: theme.surface, color: team.disabledAt ? theme.accent : theme.status.danger.fg,
                }}
              >
                {team.disabledAt ? 'Enable team' : 'Disable team'}
              </button>
            </div>
          )}
          {showAddForm && available.length > 0 && (
            <form onSubmit={handleAdd} style={{
              padding: '10px 16px', display: 'flex', flexWrap: 'wrap', gap: 8,
              borderBottom: `1px solid ${theme.border}`,
            }}>
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                required style={{ ...inputStyle, flex: '1 1 200px', minWidth: 0 }}
              >
                <option value="">Add member…</option>
                {available.map((m: OrgMember) => (
                  <option key={m.userId} value={m.userId}>
                    {m.displayName}{m.email && sameName(m) ? ` (${m.email})` : ''}{m.teamName ? ` — on ${m.teamName} (move)` : ''}
                  </option>
                ))}
              </select>
              {isAdmin && (
                <select value={role} onChange={(e) => setRole(e.target.value as TeamRole)} style={{ ...inputStyle, width: 100 }}>
                  <option value="member">Member</option>
                  <option value="deputy">Deputy</option>
                  <option value="leader">Leader</option>
                </select>
              )}
              <button type="submit" disabled={addMut.isPending} style={smallSubmit}>
                {addMut.isPending ? '…' : 'Add'}
              </button>
            </form>
          )}
          {showAddForm && available.length === 0 && (
            <div style={{ padding: '10px 16px', fontSize: 13, color: theme.textMuted }}>
              {isAdmin
                ? 'All org members are already on this team.'
                : 'Everyone is already on a team — ask an admin to move people between teams.'}
            </div>
          )}

          {members.length === 0 && <EmptyMsg>No members yet.</EmptyMsg>}
          {members.map((m, i) => (
            <div
              key={m.userId}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 20px',
                borderBottom: i < members.length - 1 ? `1px solid ${theme.border}` : 'none',
              }}
            >
              <div style={{
                width: 30, height: 30, borderRadius: 15,
                background: theme.surface, border: `1px solid ${theme.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700, color: theme.textDim, flexShrink: 0,
              }}>
                {m.displayName.slice(0, 2).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, color: leaveOf(m.userId)?.active ? theme.textMuted : theme.text, fontWeight: 500 }}>
                  {m.displayName}
                </div>
                {leaveOf(m.userId) && <div style={{ marginTop: 2 }}><LeaveTag leave={leaveOf(m.userId)!} /></div>}
              </div>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
                background: m.role !== 'member' ? theme.status.service.bg : theme.status.muted.bg,
                color: m.role !== 'member' ? theme.status.service.fg : theme.status.muted.fg,
              }}>
                {m.role.toUpperCase()}
              </span>
              {/* Leaders/deputies can't remove leaders or deputies (incl. themselves) — that's an admin call. */}
              {(isAdmin || m.role === 'member') && (
                <button aria-label="Remove from team"
                  onClick={() => removeMut.mutate(m.userId)}
                  title="Remove from team"
                  style={{
                    width: 26, height: 26, borderRadius: 6,
                    border: `1px solid ${theme.border}`,
                    background: theme.surface, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: theme.textMuted,
                  }}
                >
                  <Icon name="trash" size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyMsg({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ padding: '20px 20px', fontSize: 13, color: theme.textMuted, textAlign: 'center' }}>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '8px 10px', background: theme.surface,
  border: `1px solid ${theme.border}`, borderRadius: 8,
  fontSize: 13, color: theme.text, fontFamily: theme.font,
  outline: 'none', boxSizing: 'border-box',
};

const addBtn: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '7px 14px', borderRadius: 8,
  background: theme.accent, border: 'none',
  color: theme.accentInk, fontSize: 13, fontWeight: 600,
  cursor: 'pointer', fontFamily: theme.font,
};

const addBtnSmall: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 4,
  padding: '5px 10px', borderRadius: 6,
  background: theme.accent, border: 'none',
  color: theme.accentInk, fontSize: 12, fontWeight: 600,
  cursor: 'pointer', fontFamily: theme.font,
};

const smallSubmit: React.CSSProperties = {
  padding: '8px 16px', borderRadius: 8,
  background: theme.accent, border: 'none',
  color: theme.accentInk, fontSize: 13, fontWeight: 700,
  cursor: 'pointer', fontFamily: theme.font, flexShrink: 0,
};
